import stateMachine from './state-machine.png'

const content = `<p>The State design pattern is useful when an object has a state, as in a state machine. It consists in representing each state by a different object that knows its transitions to other states.</p>

<p>The State pattern increases <a href="/high-cohesion">cohesion</a> in the code by separating the logic of state handling from the code that holds the state, and by separating each state from the others.</p>

<h2 id="Mixing-state-and-its-usage">Mixing state and its usage</h2>

<p>Let’s take an example to illustrate.</p>

<p>In a task manager application, a task can go through the following steps: backlog, under dev, testable and done. Tasks in the backlog can go directly to testable (if there was little or no development), and testable tasks can go back to under dev (if the test failed).</p>

<p>Here is a diagram representing those states and their transitions:</p>

<img src=${stateMachine} alt="state-machine" />

<p>One way to implement this state machine is to have the Task handle the state:</p>

<pre><code class="language-ruby">class Task
  def initialize
    @status = 'backlog'
  end

  def start_dev
    if (@status == 'backlog')
      @status = 'under_dev'
    end
  end

  def dev_finished
    if (@status == 'backlog' || @status == 'under_dev')
      @status = 'testable'
    end
  end

  def test_successful
    if (@status == 'testable')
      @status = 'done'
    end
  end

  def test_failed
    if (@status == 'testable')
      @status = 'under_dev'
    end
  end
end</code></pre>

<p>An issue with this design is that the <code>Task</code> likely has other concerns that state management, especially in a task manager application. This makes the <code>Task</code> contain several concerns, meaning that it has low cohesion.</p>

<h2 id="Separating-states-with-the-State-pattern">Separating states with the State pattern</h2>

<p>The State pattern relieves the class from the state handling logic by introducing classes that handle each state. We’d have a class for the Backlog state, that has two possible transitions, <code>start_dev</code> and <code>dev_finished</code>, that return their destination states:</p>

<pre><code class="language-ruby">class Backlog
  def start_dev
    UnderDev.new
  end

  def dev_finished
    Testable.new
  end
end</code></pre>

<p>One for the Under dev state:</p>

<pre><code class="language-ruby">class UnderDev
  def dev_finished
    Testable.new
  end
end</code></pre>

<p>And similarly for the Testable and Done states:</p>

<pre><code class="language-ruby">class Testable
  def test_successful
    Done.new
  end

  def test_failed
    UnderDev.new
  end
end

class Done
end</code></pre>

<p>The State pattern removes all the logic based on if statements that was in the <code>Task</code> class. The task now forwards the operations related to state to the new classes:</p>

<pre><code class="language-ruby">class Task
  def initialize
    @status = Backlog.new
  end

  def start_dev
    @status = @status.start_dev
  end

  def dev_finished
    @status = @status.dev_finished
  end

  def test_successful
    @status = @status.test_successful
  end

  def test_failed
    @status = @status.test_failed
  end
end</code></pre>

<p>In a different style, this can be condensed in one line functions definitions:</p>

<pre><code class="language-ruby">class Task
  def initialize
    @status = Backlog.new
  end

  def start_dev;       @status = @status.start_dev       end
  def dev_finished;    @status = @status.dev_finished    end
  def test_successful; @status = @status.test_successful end
  def test_failed;     @status = @status.test_failed     end
end
</code></pre>

<p>The <code>Task</code> class has better cohesion at its implementation focuses on its other task concerns (not represented in this example).</p>

<p>This also has implications on testing: the State pattern allows to test the state transitions independently of the <code>Task</code> class.</p>

<h2 id="Changing-state-logic">Changing state logic</h2>

<p>The State pattern reduced the coupling between <code>Task</code> and state management. This means that changes of state logic only impacts the code the state classes, and <code>Task</code> doesn’t have to change.</p>

<p>Let’s take an example of state machine change in the Ruby language. Let’s say that when a test fails, we want the task to go back to Backlog instead of Under dev. Then that change happens in the <code>Testable</code> class, not in <code>Task</code>.</p>

<p>Changes where the State pattern has even more impact are those that concern all states. Let’s take the example of error handling. When the code tries to perform an invalid transition, for example passing a <code>test_successful</code> operation on a task that is <code>UnderDev</code>, we want an exception with the following message: <code>"test_successful cannot be applied on UnderDev tasks"</code>.</p>

<p>With the initial code, this would have meant to add an <code>else</code> to all state functions. That adds more state-related code to <code>Task</code>:</p>

<pre><code class="language-ruby">class Task
  def initialize
    @status = 'backlog'
  end

  def start_dev
    if (@status == 'backlog')
      @status = 'under_dev'
    else
      raise "start_dev cannot be applied to #{@status} tasks"
    end
  end

  def dev_finished
    if (@status == 'backlog' || @status == 'under_dev')
      @status = 'testable'
    else
      raise "dev_finished cannot be applied to #{@status} tasks"
    end
  end

  def test_successful
    if (@status == 'testable')
      @status = 'done'
    else
      raise "test_successful cannot be applied to #{@status} tasks"
    end
  end

  def test_failed
    if (@status == 'testable')
      @status = 'under_dev'
    else
      raise "test_failed cannot be applied to #{@status} tasks"
    end
  end

  attr_reader :status
end</code></pre>

<p>With the State pattern, this type of feature common to all states can be implemented once in a base class:</p>

<pre><code class="language-ruby">class TaskState
  def method_missing(method, *args)
    raise "State #{self.class.name} does't have transition #{method.name} tasks"
  end
end</code></pre>

<p>And all the state classes only have to inherit from that base class to get that error handling feature without adding code to each state:</p>

<pre><code class="language-ruby">class Backlog &lt; TaskState
  def start_dev
    UnderDev.new
  end

  def dev_finished
    Testable.new
  end
end

class UnderDev &lt; TaskState
  def dev_finished
    Testable.new
  end
end

class Testable &lt; TaskState
  def test_successful
    Done.new
  end

  def test_failed
    UnderDev.new
  end
end

class Done &lt; TaskState
end</code></pre>

<p>And the <code>Task</code> class doesn’t have to be changed.</p>

<p>The State pattern can take other forms, for example the whole <code>Task</code> class can represent its state instead of holding it. We’d have a <code>BacklogTask</code> class instead of a <code>Backlog</code> state, and so on. This allows the rest of the task class definition to be different depending on the state.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>The State pattern consists in creating one class for each state</li><li>The state classes handle their transitions and everything else related to state</li><li>The user of state no longer have state logic</li><li>This increases its cohesion and decreases its coupling with state logic</li></ul>
</div>`

export default content
